import React from 'react';

/** Marketing site origin for legal pages (privacy, terms, cookies). */
export const MARKETING_ORIGIN = 'https://clicktube.co.il';

/** URL for pricing / subscribe. Change here to update all app links to the payment page. */
export const PAYMENT_PAGE_URL = 'https://app.clicktube.co.il';

import { 
  Zap, 
  LayoutGrid, 
  Plus, 
  Image as ImageIcon, 
  User, 
  Video, 
  Palette, 
  Instagram, 
  Youtube,
  Upload,
  MessageSquare,
  Download,
  ArrowRight,
  Loader2,
  Trash2,
  Edit2,
  FlaskConical,
  Check,
  Settings,
  Wand2,
  Save,
  X,
  Type,
  RotateCw,
  RotateCcw,
  Move,
  Maximize,
  Layers,
  Sun,
  Copy,
  Bookmark,
  Sparkles,
  AlignCenterHorizontal,
  AlignCenterVertical,
  LogIn,
  LogOut,
  Crown,
  Coins,
  AlertTriangle,
  Menu
} from 'lucide-react';
import { TextStyleTemplate } from './types';

export const ICONS = {
  Zap,
  LayoutGrid,
  Plus,
  Image: ImageIcon,
  User,
  Video,
  Palette,
  Instagram,
  Youtube,
  Upload,
  MessageSquare,
  Download,
  ArrowRight,
  Loader2,
  Trash2,
  Edit2,
  FlaskConical,
  Check,
  Settings,
  Wand2,
  Save,
  X,
  Type,
  RotateCw,
  RotateCcw,
  Move,
  Maximize,
  Layers,
  Sun,
  Copy,
  Bookmark,
  Sparkles,
  AlignHorizontal: AlignCenterHorizontal,
  AlignVertical: AlignCenterVertical,
  LogIn,
  LogOut,
  Crown,
  Coins,
  AlertTriangle,
  Menu
};

export const HEBREW_FONTS = [
  { name: 'Rubik (ברירת מחדל)', value: 'Rubik', family: 'Rubik, sans-serif' },
  { name: 'Heebo (מודרני ונקי)', value: 'Heebo', family: 'Heebo, sans-serif' },
  { name: 'Assistant (קריא מאוד)', value: 'Assistant', family: 'Assistant, sans-serif' },
  { name: 'Secular One (כותרות בולטות)', value: 'Secular One', family: '"Secular One", sans-serif' },
  { name: 'Varela Round (עגול וידידותי)', value: 'Varela Round', family: '"Varela Round", sans-serif' },
  { name: 'Amatic SC (כתב יד)', value: 'Amatic SC', family: '"Amatic SC", cursive' },
  { name: 'Orbitron (עתידני)', value: 'Orbitron', family: '"Orbitron", sans-serif' },
];

export const STYLE_PREVIEWS: Record<string, string> = {
  'ריאליסטי': 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=400&auto=format&fit=crop',
  'ויראלי / הייפ': 'https://images.unsplash.com/photo-1549417229-aa67d3263c09?q=80&w=400&auto=format&fit=crop',
  'עסקי נקי': 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=400&auto=format&fit=crop',
  'ניאון וגיימינג': 'https://images.unsplash.com/photo-1535295972055-1c762f4483e5?q=80&w=400&auto=format&fit=crop',
  'קולנועי דרמטי': 'https://images.unsplash.com/photo-1533105079780-92b9be482077?q=80&w=400&auto=format&fit=crop',
  'אסתטי טבעי': 'https://images.unsplash.com/photo-1616046229478-9901c5536a45?q=80&w=400&auto=format&fit=crop',
  'רטרו ונוסטלגיה': 'https://images.unsplash.com/photo-1598550476439-6847785fcea6?q=80&w=400&auto=format&fit=crop',
  'קולאז\' אומנותי': 'https://images.unsplash.com/photo-1572204387542-d35d27d92f75?q=80&w=400&auto=format&fit=crop',
  'מחוספס וחזק': 'https://images.unsplash.com/photo-1509114397022-ed747cca3f65?q=80&w=400&auto=format&fit=crop',
  'קומיקס': 'https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?q=80&w=400&auto=format&fit=crop',
  'מיינקראפט': 'https://images.unsplash.com/photo-1587573089734-09cb69c0f2b4?q=80&w=400&auto=format&fit=crop',
  'מינימליסטי': 'https://images.unsplash.com/photo-1507643179173-617d654f3daf?q=80&w=400&auto=format&fit=crop',
  'אנימה': 'https://images.unsplash.com/photo-1578632767115-351597cf2477?q=80&w=400&auto=format&fit=crop',
  'סוריאליסטי': 'https://images.unsplash.com/photo-1534312527009-56c7016453e6?q=80&w=400&auto=format&fit=crop',
  'אחר': 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?q=80&w=400&auto=format&fit=crop',
};

export const DEFAULT_TEXT_STYLES: TextStyleTemplate[] = [
  {
    id: 'default_bold_white',
    name: 'לבן נקי',
    isDefault: true,
    style: {
      color: '#FFFFFF',
      fontFamily: 'Rubik, sans-serif',
      fontSize: 100,
      strokeWidth: 0,
      isBackgroundEnabled: false,
      isShadowEnabled: true,
      shadowColor: '#000000',
      shadowOpacity: 0.8,
      shadowBlur: 10,
      shadowDistance: 5,
      shadowAngle: 45,
      neonIntensity: 0
    }
  },
  {
    id: 'default_neon_red',
    name: 'ניאון אדום',
    isDefault: true,
    style: {
      color: '#FFFFFF',
      fontFamily: 'Heebo, sans-serif',
      fontSize: 100,
      strokeWidth: 0,
      isBackgroundEnabled: false,
      isShadowEnabled: false,
      neonColor: '#FF003C',
      neonIntensity: 30
    }
  },
  {
    id: 'default_yellow_black_stroke',
    name: 'צהוב בולט',
    isDefault: true,
    style: {
      color: '#FFD700',
      fontFamily: '"Secular One", sans-serif',
      fontSize: 110,
      strokeColor: '#000000',
      strokeWidth: 15,
      isBackgroundEnabled: false,
      isShadowEnabled: true,
      shadowColor: '#000000',
      shadowOpacity: 1,
      shadowBlur: 0,
      shadowDistance: 10,
      shadowAngle: 45,
      neonIntensity: 0
    }
  },
    {
    id: 'default_black_bg',
    name: 'רקע שחור',
    isDefault: true,
    style: {
      color: '#FFFFFF',
      fontFamily: 'Assistant, sans-serif',
      fontSize: 90,
      strokeWidth: 0,
      isBackgroundEnabled: true,
      backgroundColor: '#000000',
      backgroundOpacity: 100,
      backgroundPadding: 20,
      backgroundRadius: 0,
      isShadowEnabled: false,
      neonIntensity: 0
    }
  }
];